/**
 * Panel 2 — Egress audit: "show me every external transmission of org X's data"
 * as a screen, not a psql session (the ADR-001 processor enumeration). Org is
 * the first pick to avoid unbounded scans. `pre_audit` rows are labelled
 * honestly — never pretended to carry an identity they never had.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../../theme';
import { Button, FilterChip, SearchInput, StatusPill } from '../../components';
import { exportExecutionsCsv, fetchOpsExecutions, OpsExecutionPage } from '../../api/ops';
import { PanelFrame, Mono, EmptyState, Loading, ErrorNote } from './OpsScreen';

const CATEGORIES = ['retryable', 'conflict', 'not_found', 'unauthorized', 'permanent'] as const;
const PAGE = 100;

const toneFor = (category: string | null): string => {
  if (category === 'unauthorized' || category === 'permanent') return 'danger';
  if (category === 'retryable') return 'warn';
  return 'neutral';
};

export default function EgressAuditPanel() {
  const [page, setPage] = React.useState<OpsExecutionPage | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [exporting, setExporting] = React.useState(false);
  const [org, setOrg] = React.useState('');
  const [connector, setConnector] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [offset, setOffset] = React.useState(0);

  const filters = React.useMemo(
    () => ({
      org: org.trim() || undefined,
      connector: connector.trim() || undefined,
      error_category: category || undefined,
    }),
    [org, connector, category],
  );

  React.useEffect(() => {
    let live = true;
    setPage(null);
    setError(null);
    fetchOpsExecutions({ ...filters, limit: PAGE, offset })
      .then((r) => live && setPage(r))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [filters, offset]);

  // Any filter change restarts paging — a stale offset would show a blank page.
  React.useEffect(() => setOffset(0), [filters]);

  const onExport = () => {
    setExporting(true);
    exportExecutionsCsv(filters)
      .catch((e: Error) => setError(`Export failed: ${e.message}`))
      .finally(() => setExporting(false));
  };

  const rows = page?.executions ?? [];

  return (
    <PanelFrame
      title="Egress audit"
      subtitle="Every external transmission of a tenant's data, by connector and operation (ADR-001 enumeration)."
      toolbar={
        <>
          <View style={{ width: 300 }}>
            <SearchInput value={org} onChange={setOrg} placeholder="Org id (pick one to scope)…" mono />
          </View>
          <View style={{ width: 180 }}>
            <SearchInput value={connector} onChange={setConnector} placeholder="Connector…" mono />
          </View>
          {CATEGORIES.map((c) => (
            <FilterChip key={c} label={c} active={category === c} onPress={() => setCategory(category === c ? '' : c)} />
          ))}
          <Button
            label={exporting ? 'Exporting…' : 'Export CSV'}
            variant="secondary"
            onPress={onExport}
            disabled={exporting || rows.length === 0}
            title={rows.length === 0 ? 'Nothing to export for this filter' : 'Download the current filter as CSV'}
          />
        </>
      }
    >
      {error ? <ErrorNote text={error} /> : null}
      {page === null && !error ? <Loading /> : null}
      {page !== null && rows.length === 0 ? <EmptyState text="No egress recorded for this filter." /> : null}
      {rows.length > 0 ? (
        <View style={styles.table}>
          <View style={[styles.row, styles.headRow]}>
            <Text style={[styles.h, styles.cTime]}>Timestamp</Text>
            <Text style={[styles.h, styles.cConn]}>Connector</Text>
            <Text style={[styles.h, styles.cOp]}>Operation</Text>
            <Text style={[styles.h, styles.cStatus]}>Status</Text>
            <Text style={[styles.h, styles.cCat]}>Error category</Text>
            <Text style={[styles.h, styles.cLat]}>Latency</Text>
            <Text style={[styles.h, styles.cId]}>Execution id</Text>
          </View>
          {rows.map((r) => (
            <View key={r.execution_id} style={styles.row}>
              <View style={styles.cTime}><Mono dim>{r.executed_at.replace('T', ' ').replace('Z', '')}</Mono></View>
              <View style={styles.cConn}>
                <Mono>{r.connector}</Mono>
                {r.pre_audit ? (
                  <View style={styles.preAudit}>
                    <Text style={styles.preAuditText}>identity not captured (pre-compliance)</Text>
                  </View>
                ) : null}
              </View>
              <View style={styles.cOp}><Mono dim>{r.canonical_tool ?? r.operation ?? '—'}</Mono></View>
              <View style={styles.cStatus}>
                <StatusPill label={r.status} tone={r.status === 'ok' ? 'success' : 'danger'} />
              </View>
              <View style={styles.cCat}>
                {r.error_category ? (
                  <StatusPill label={r.error_category} tone={toneFor(r.error_category)} dot={false} />
                ) : (
                  <Text style={styles.subtle}>—</Text>
                )}
              </View>
              <View style={styles.cLat}>
                <Text style={styles.latency}>{r.latency_ms === null ? '—' : `${r.latency_ms} ms`}</Text>
              </View>
              <Pressable
                style={styles.cId}
                onPress={() => navigator.clipboard?.writeText(r.execution_id)}
                {...({ title: 'Copy execution id' } as object)}
              >
                <Mono dim>{r.execution_id.slice(0, 8)}…</Mono>
              </Pressable>
            </View>
          ))}
        </View>
      ) : null}
      {page !== null ? (
        <View style={styles.footerRow}>
          <Text style={styles.footnote}>
            {page.total} row{page.total === 1 ? '' : 's'} in range
            {/* Honest about what an org filter cannot include. */}
            {page.pre_audit_in_range > 0
              ? ` · ${page.pre_audit_in_range} pre-compliance row${page.pre_audit_in_range === 1 ? '' : 's'} carry no org identity${filters.org ? ' and are excluded from this org-filtered view' : ''}`
              : ''}
          </Text>
          <View style={{ flex: 1 }} />
          {page.total > PAGE ? (
            <View style={styles.pager}>
              <Button
                label="Previous"
                variant="secondary"
                onPress={() => setOffset(Math.max(0, offset - PAGE))}
                disabled={offset === 0}
                title={offset === 0 ? 'Already on the first page' : 'Previous page'}
              />
              <Text style={styles.footnote}>
                {offset + 1}–{Math.min(offset + PAGE, page.total)}
              </Text>
              <Button
                label="Next"
                variant="secondary"
                onPress={() => setOffset(offset + PAGE)}
                disabled={offset + PAGE >= page.total}
                title={offset + PAGE >= page.total ? 'Already on the last page' : 'Next page'}
              />
            </View>
          ) : null}
        </View>
      ) : null}
    </PanelFrame>
  );
}

const styles = StyleSheet.create({
  table: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, backgroundColor: colors.card, overflow: 'hidden' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 9,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: colors.line,
  },
  headRow: { backgroundColor: colors.paper2 },
  h: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink3, textTransform: 'uppercase' as any },
  subtle: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink4 },
  latency: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink2, textAlign: 'right' },
  cTime: { flex: 1.8 },
  cConn: { flex: 1.5 },
  cOp: { flex: 1.5 },
  cStatus: { flex: 0.9 },
  cCat: { flex: 1.1 },
  cLat: { flex: 0.8, alignItems: 'flex-end' },
  cId: { flex: 0.9, cursor: 'pointer' } as any,
  preAudit: {
    marginTop: 3,
    alignSelf: 'flex-start',
    paddingVertical: 1,
    paddingHorizontal: 6,
    borderRadius: radii.full,
    borderWidth: 1,
    borderColor: colors.neutralLine,
    backgroundColor: colors.neutralSoft,
  },
  preAuditText: { fontFamily: fontFamily.sans, fontSize: 10, color: colors.ink3 },
  footerRow: { flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  footnote: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink4 },
  pager: { flexDirection: 'row', alignItems: 'center', gap: 8 },
});
